import { Component } from 'preact';
import { LAMBDA } from '@globals';
import root from '@components/root';
import find from 'dlv';

const namespace = 'TableOnline--auth';
const defaultRequestOptions = { method: 'GET' };
const statusCodes = {
  200: 'OK',
  201: 'Created',
  202: 'Accepted',
  204: 'No Content',
  301: 'Moved Permanently',
  302: 'Found',
  303: 'See Other',
  304: 'Not Modified',
  307: 'Temporary Redirect',
  400: 'Bad Request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not Found',
  405: 'Method Not Allowed',
  406: 'Not Acceptable',
  412: 'Precondition Failed',
  415: 'Unsupported Media Type',
  500: 'Internal Server Error',
  501: 'Not Implemented',
};

// BUNDLE FETCH REQUESTS (only GET)
const MEMFETCH = {};

// MEMOIZE RESPONSES (only GET)
const MEMRESPONSE = {};

const b64DecodeUnicode = str => {
  // Going backwards: from bytestream, to percent-encoding, to original string.
  return decodeURIComponent(
    atob(str)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );
};
let saveData = (function () {
  var a = document.createElement('a');
  document.body.appendChild(a);
  a.style = 'display: none';
  return function (data, fileName) {
    var blob = new Blob([data], { type: 'octet/stream' }),
      url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  };
})();

class AuthProvider extends Component {
  _fetch(url, opts, token) {
    return new Promise((resolve, reject) => {
      const options = { ...defaultRequestOptions, ...opts };
      const INTERNALREQUEST =
        url.indexOf('https://') !== 0 && url.indexOf('http://') !== 0;
      const path = (INTERNALREQUEST ? LAMBDA : '') + url;

      if (INTERNALREQUEST) {
        if (typeof options.body === 'string')
          options.headers = {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          };
        if (token) {
          options.headers = { ...options.headers, Authorization: token };
        }
        options.headers = {
          ...options.headers,
          Platform: 'web',
        };
      }
      if (opts && opts.headers)
        options.headers = { ...options.headers, ...opts.headers };

      if (options.body && typeof options.body === 'string') {
        // CLEAN ILLEGAL CHARACTERS, EVEN IF IM NOT THE POLICE
        options.body = options.body.replace(/[\u200B-\u200D\uFEFF]/g, '');
      }

      if (options.method === 'GET' && MEMRESPONSE[path]) {
        resolve(MEMRESPONSE[path]);
      } else if (options.method === 'GET' && MEMFETCH[path]) {
        MEMFETCH[path].push({ resolve, reject });
      } else {
        if (options.method === 'GET') MEMFETCH[path] = [];
        fetch(path, options)
          .then(async response => {
            if (response.ok) {
              if (find(options, 'headers.Accept', '').includes('text/')) {
                saveData(await response.text(), url.split('/').pop() + '.csv');
              } else return response.json();
            } else {
              return new Promise((resolve, reject) => {
                response
                  .json()
                  .then(json => {
                    reject(json);
                  })
                  .catch(() => {
                    reject(statusCodes[response.status]);
                  });
              });
            }
          })
          .then(response => {
            if (typeof response === 'string') {
              throw response;
            }

            if (response.errorMessage) {
              if (response.errorMessage.includes('Task timed out')) {
                //toast('Server is not responding, please try again', {type: 'error'} );
              } else {
                //toast(response.errorMessage, {type: 'error'} );
              }
              reject(response.errorMessage);
              if (options.method === 'GET')
                MEMFETCH[path].forEach(callback =>
                  callback.reject(response.errorMessage)
                );
            } else if (response.error) {
              reject(response.error);
              if (options.method === 'GET')
                MEMFETCH[path].forEach(callback =>
                  callback.reject(response.errorMessage)
                );
            } else {
              resolve(response);
              if (options.method === 'GET') MEMRESPONSE[path] = response;
              if (options.method === 'GET')
                MEMFETCH[path].forEach(callback => callback.resolve(response));
            }
            if (options.method === 'GET') delete MEMFETCH[path];
          })
          .catch(err => {
            if (err.indexOf && err.includes('Task timed out')) {
              //toast('Server is not responding, please try again', {type: 'error'} );
            } else if (typeof err === 'string') {
              //toast(err, {type: 'error'} );
            } else {
              //toast('An error occured while fetching assets', {type: 'error'} );
            }
            reject(err);
            if (options.method === 'GET')
              MEMFETCH[path].forEach(callback => callback.reject(err));
            if (options.method === 'GET') delete MEMFETCH[path];
          });
      }
    });
  }
  _decodeJWTData = jwt => {
    return jwt ? JSON.parse(b64DecodeUnicode(jwt.split('.')[1])) : {};
  };
  isValidToken = (token, sub) => {
    const data = this._decodeJWTData(token);
    return data && data.sub === sub && new Date().valueOf() < data.exp * 1000;
  };
  shouldFetchRefreshToken = token => {
    const data = this._decodeJWTData(token);
    // Decide if token should be refreshed ( set to 7 days from now )
    return new Date().valueOf() + 1000 * 60 * 60 * 24 * 7 > data.exp * 1000;
  };
  getCurrentAccessToken = () => {
    return new Promise((resolve, reject) => {
      const user = this.getUser();
      if (!user) {
        resolve();
        return;
      }
      let token = user.accessToken;
      const refreshToken = user.refreshToken;

      if (!this.isValidToken(token, 'access')) {
        this.getAccessToken(refreshToken)
          .then(() => {
            token = this.getUser().accessToken;
            resolve(token);
          })
          .catch(err => {
            reject(err);
          });
      } else {
        resolve(token);
      }
    });
  };
  getToken = (token, returnSecureToken) => {
    return new Promise((resolve, reject) => {
      if (this._tokenFetch) {
        this._tokenFetch.push({ resolve, reject });
      } else {
        this._tokenFetch = [];
        this._fetch('/auth/token', {
          method: 'POST',
          body: JSON.stringify({
            token,
            returnSecureToken,
          }),
        })
          .then(res => {
            resolve(res);
            this._tokenFetch.forEach(callbacks => callbacks.resolve(res));
            this._tokenFetch = null;
          })
          .catch(err => {
            reject(err);
            this._tokenFetch.forEach(callbacks => callbacks.reject(err));
            this._tokenFetch = null;
          });
      }
    });
  };
  getRefreshToken = token => {
    return new Promise((resolve, reject) => {
      if (!this.isValidToken(token, 'refresh')) {
        this.logout();
        return reject('Unauthorized');
      }

      this.getToken(token, true)
        .then(response => {
          // Save latest refresh token to user data
          this.setUser({ ...this.getUser(), refreshToken: response.token });
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    });
  };
  loginWithCustomToken = refreshToken => {
    return new Promise((resolve, reject) => {
      this.getAccessToken(refreshToken)
        .then(() => {
          this.setUser({ refreshToken });
          this.getUserFromServer().then(res => {
            this.context.events.emit('loggedIn', true);
            resolve(res);
          });
        })
        .catch(err => {
          reject(err);
        });
    });
  };
  getAccessToken = refreshtoken => {
    return new Promise((resolve, reject) => {
      if (!this.isValidToken(refreshtoken, 'refresh')) {
        this.logout();
        return reject('Unauthorized');
      }

      this.getToken(refreshtoken)
        .then(response => {
          // Save latest token to user data
          const user =
            response.user ||
            this._decodeJWTData(response.token) ||
            this.getUser() ||
            {};

          user.accessToken = response.token;
          if (response.rt) user.refreshToken = response.rt;
          else if (refreshtoken) user.refreshToken = refreshtoken;

          this.setUser(user);
          resolve(response);
        })
        .catch(error => {
          this.logout();
          reject(error);
        });
    });
  };
  resetPassword = auth => {
    return new Promise((resolve, reject) => {
      this._fetch('/auth/resetpass', {
        method: 'POST',
        body: JSON.stringify({
          ...auth,
          lang: window.location.pathname
            .split('/')
            .reduce((r, item) => r || item, ''),
        }),
      })
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  };
  getUser = () => {
    const str = localStorage.getItem(namespace);
    return str ? JSON.parse(str) : null;
  };
  getUserFromServer = () => {
    return new Promise((resolve, reject) => {
      const user = this.getUser();
      if (user) {
        this.getCurrentAccessToken()
          .then(accessToken => {
            if (user.accessToken) {
              this._fetch(
                '/user',
                {
                  method: 'GET',
                },
                accessToken
              )
                .then(res => {
                  if (typeof res === 'string') {
                    throw res;
                  }
                  this.setUser(
                    res.restaurants
                      ? { ...res.user, restaurants: res.restaurants }
                      : res.user
                  );
                  resolve(res.user);
                })
                .catch(err => {
                  reject(err);
                });
            }
          })
          .catch(err => {
            this.logout();
            reject(err);
          });
      } else {
        reject('User is not logged in');
      }
    });
  };
  setUser = changes => {
    let user = this.getUser() || {};
    if (user.accessToken) {
      user = { ...user, ...this._decodeJWTData(user.accessToken) };
    }
    Object.keys(changes).forEach(key => {
      user[key] = changes[key];
    });

    localStorage.setItem(namespace, JSON.stringify(user));

    this.context.events.emit('auth', user);

    if (user.email && root.Raven) {
      window.Raven.this.setUserContext({
        email: user.email,
      });
    }

    return user;
  };
  createAccount = auth => {
    return new Promise((resolve, reject) => {
      this._fetch('/auth/signup', {
        method: 'POST',
        body: JSON.stringify(auth),
      })
        .then(res => resolve(res))
        .catch(error => {
          reject(error);
        });
    });
  };
  deleteAccount = () => {
    return this.request('/auth/delete', {
      method: 'DELETE',
    });
  };
  login = auth => {
    return new Promise((resolve, reject) => {
      this._fetch('/auth/login', {
        method: 'POST',
        body: JSON.stringify(auth),
      })
        .then(res => {
          this.loginWithCustomToken(res.token).then(resolve).catch(reject);
        })
        .catch(error => {
          reject(error);
        });
    });
  };
  logout = () => {
    localStorage.removeItem(namespace);
    this.context.events.emit('auth', null);
    this.context.events.emit('loggedIn', false);
  };
  update = user => {
    return new Promise((resolve, reject) => {
      if (user) {
        this.getCurrentAccessToken()
          .then(accessToken => {
            this._fetch(
              '/user',
              {
                method: 'POST',
                body: JSON.stringify(user),
              },
              accessToken
            )
              .then(res => {
                this.getAccessToken(res.token).then(resolve);
              })
              .catch(error => {
                reject(error);
              });
          })
          .catch(err => {
            this.logout();
            reject(err);
          });
      } else {
        reject('User is not logged in');
      }
    });
  };
  request = (url, opts) => {
    return new Promise((resolve, reject) => {
      this.getCurrentAccessToken()
        .then(token => {
          this._fetch(url, opts, token)
            .then(res => {
              resolve(res);
            })
            .catch(err => {
              reject(err);
            });
        })
        .catch(err => {
          reject(err);
        });
    });
  };
  init = async () => {
    const user = this.getUser();
    if (
      user &&
      user.refreshToken &&
      this.isValidToken(user.refreshToken, 'refresh')
    ) {
      if (this.shouldFetchRefreshToken(user.refreshToken)) {
        try {
          await this.getRefreshToken(user.refreshToken);
        } catch (error) {
          this.logout();
          return;
        }
      }

      if (user.accessToken && this.isValidToken(user.accessToken, 'access')) {
        this.context.events.emit('auth', user);
        this.context.events.emit('loggedIn', true);
        this.getUserFromServer();
      } else {
        this.getAccessToken(user.refreshToken)
          .then(this.init)
          .catch(() => {
            this.logout();
          });
      }
    } else {
      this.context.events.emit('loggedIn', false);
    }
    return this;
  };

  componentDidMount() {
    this.init();
  }
  getChildContext() {
    let {
      logout,
      getUser,
      setUser,
      request,
      createAccount,
      deleteAccount,
      login,
      resetPassword,
      update,
      getRefreshToken,
    } = this;
    let auth = {
      logout,
      getUser,
      setUser,
      request,
      createAccount,
      deleteAccount,
      login,
      resetPassword,
      update,
      getRefreshToken,
    };
    return { auth };
  }
  render({ children }) {
    return children[0];
  }
}

export default AuthProvider;



// WEBPACK FOOTER //
// ./utils/auth.js